import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { format } from 'date-fns';
import { config } from '../../config';
import ZoobbeSelect from '../Global/ZoobbeSelect';
import './scss/CardDetailsModal.scss';
import PriorityFlag from '../icons/PriorityFlag';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import Close from '../icons/Close';

const CardDetailsModal = ({ isOpen, onClose, boardId, cardType, title, totalCards }) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const [cardSizes, setCardSizes] = useState(new Map());
  const { handlePopoverClick } = useHandlePopoverClick();

  // Fetch cards based on type
  useEffect(() => {
    if (isOpen && boardId && cardType) {
      setCards([]);
      setOffset(0);
      setHasMore(true);
      fetchCards(true);
    }
  }, [isOpen, boardId, cardType, filter, sortBy, searchTerm]);

  const fetchCards = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const currentOffset = reset ? 0 : offset;
      const limit = 20;

      let endpoint = '';
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString()
      });

      // Add search parameter
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Add filter parameters
      switch (cardType) {
        case 'completed':
          params.append('completed', 'true');
          break;
        case 'overdue':
          params.append('overdue', 'true');
          break;
        default:
          // For 'total', no additional filter needed
          break;
      }

      // Add additional filters
      if (filter === 'high-priority') {
        params.append('priority', 'High');
      }

      endpoint = `/v1/boards/${boardId}/cards?${params.toString()}`;

      const response = await fetch(`${config.API_URI}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const newCards = data.cards || [];

        if (reset) {
          setCards(newCards);
          setOffset(newCards.length);
        } else {
          setCards(prev => [...prev, ...newCards]);
          setOffset(prev => prev + newCards.length);
        }

        setHasMore(data.pagination?.hasMore || false);
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [boardId, cardType, filter, searchTerm, offset]);

  // Calculate dynamic card height
  const calculateCardHeight = useCallback((card) => {
    const baseHeight = 100;
    const descriptionHeight = card.description ? Math.min(Math.ceil(card.description.length / 80) * 20, 40) : 0;
    const assigneesHeight = card.users?.length > 0 ? 24 : 0;
    const metaHeight = 20;

    return baseHeight + descriptionHeight + assigneesHeight + metaHeight;
  }, []);

  // Update card sizes when cards change
  useEffect(() => {
    const newSizes = new Map();
    cards.forEach((card, index) => {
      const height = calculateCardHeight(card);
      newSizes.set(index, height);
      // Store hint on card object for virtualizer
      card.sizeHint = height;
    });
    setCardSizes(newSizes);
  }, [cards, calculateCardHeight]);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchCards(false);
    }
  }, [loadingMore, hasMore, fetchCards]);

  const handleCardClick = (card) => {
    navigate(`/c/${card.shortId}`);
    onClose();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return '#ff4757';
      case 'High': return '#ffa502';
      case 'Normal': return '#3742fa';
      case 'Low': return '#747d8c';
      default: return '#747d8c';
    }
  };

  const getStatusColor = (card) => {
    if (card.completed) return '#2ed573';
    if (card.dueDate?.date && new Date(card.dueDate.date) < new Date()) return '#ff4757';
    return '#3742fa';
  };

  // Card item component for virtualization
  const CardItem = ({ card }) => {
    return (
      <div className="card-item-wrapper">
        <div
          className="card-item"
          onClick={() => handleCardClick(card)}
        >
          <div className="card-main">
            <div className="card-header">
              <div className="card-status">
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(card) }}
                />
                <span className="card-title">{card.title || 'Untitled Card'}</span>
              </div>
              <div className="card-meta">
                <span className="card-id">#{card.cardNumber}</span>
              </div>
            </div>


            <div className="card-footer">
              <div className="card-info">
                <span className="list-name">{card.actionListTitle}</span>
                {card.dueDate?.date && (
                  <span className="due-date">
                    Due: {format(new Date(card.dueDate.date), 'MMM dd, yyyy')}
                  </span>
                )}

              </div>

              <div className="card-actions">
                {card.users?.length > 0 && (
                  <div className="assignees">
                    {card.users.slice(0, 3).map((user, idx) => (
                      <div key={idx} className="assignee-avatar">
                        {user.profilePicture ? (
                          <img src={user.profilePicture} alt={user.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {(user.name || user.username || 'U').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    ))}
                    {card.users.length > 3 && (
                      <span className="more-assignees">+{card.users.length - 3}</span>
                    )}
                  </div>
                )}

                {card.priority && (
                  <span
                    className="priority-badge"
                    data-tooltip-content={card.priority}
                    data-tooltip-position="top"
                  // onClick={(e) => handlePopoverClick(e, 'cardPriority', { cardId: card.shortId })}
                  >
                    <PriorityFlag priority={card.priority} size={14} />
                  </span>
                )}

                <span className="created-date">
                  {format(new Date(card.createdAt), 'MMM dd')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Tanstack Virtual setup
  const parentRef = useRef();

  const virtualizer = useVirtualizer({
    count: cards.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => cards[index]?.sizeHint || 120,
    overscan: 5
  });

  const measureCardSize = (el, index) => {
    if (el) {
      const height = el.getBoundingClientRect().height;
      virtualizer.measureElement(el);
      if (Object.isExtensible(cards[index])) {
        cards[index].sizeHint = height;
      }
    }
  };



  // Infinite scroll effect
  useEffect(() => {
    const [lastItem] = [...virtualizer.getVirtualItems()].reverse();

    if (!lastItem) return;

    if (
      lastItem.index >= cards.length - 1 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      loadMore();
    }
  }, [virtualizer.getVirtualItems(), cards.length, hasMore, loadingMore, loading, loadMore]);

  if (!isOpen) return null;

  return (
    <div className="card-details-modal-overlay" onClick={onClose}>
      <div className="card-details-modal" onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <div className="header-content">
            <h2>{title}</h2>
            <p className="card-count">Showing {cards.length} out of {totalCards} cards</p>

          </div>
          <div className="header-actions">
            <button className="btn-icon btn-close" onClick={onClose}>
              <Close size={24} />
            </button>
          </div>
        </div>

        <div className="modal-controls">
          <div className="search-bar">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-controls">
            <ZoobbeSelect
              options={[
                { value: 'all', label: 'All Cards' },
                { value: 'high-priority', label: 'High Priority' },
                { value: 'with-due-date', label: 'With Due Date' }
              ]}
              defaultSelectedOption={0}
              onSelect={(option) => setFilter(option.value)}
              className="filter-select"
            />

            <ZoobbeSelect
              options={[
                { value: 'recent', label: 'Most Recent' },
                { value: 'priority', label: 'Priority' },
                { value: 'due-date', label: 'Due Date' }
              ]}
              defaultSelectedOption={0}
              onSelect={(option) => setSortBy(option.value)}
              className="sort-select"
            />
          </div>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading cards...</p>
            </div>
          ) : cards.length > 0 ? (
            <div
              ref={parentRef}
              className="cards-list"
              style={{
                height: '400px', // You can calculate dynamic height like ActionListPND if needed
                overflow: 'auto',
                padding: '16px 32px',
                maxHeight: 'fit-content',
              }}
            >
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                <ol
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualizer.getVirtualItems()[0]?.start ?? 0}px)`,
                    width: '100%',
                    padding: '0',
                    margin: '0',
                    overflow: 'hidden',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => (
                    <li
                      key={cards[virtualItem.index]?._id ?? `virtual-${virtualItem.index}`}
                      data-index={virtualItem.index}
                      ref={(el) => measureCardSize(el, virtualItem.index)}
                    >
                      <CardItem card={cards[virtualItem.index]} />
                    </li>
                  ))}
                </ol>
              </div>
            </div>

          ) : (
            <div className="empty-state">
              <span className="material-symbols-outlined">task_alt</span>
              <h3>No cards found</h3>
              <p>Try adjusting your filters or search terms</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardDetailsModal;
